import React, { useRef, useState, useEffect } from 'react'
import { OfferBlock, OfferBlockHeading } from '../../models/shared/Offer'
import ContentEditable from 'react-contenteditable';
import BlockToolbar, { HeadingLevelPicker, ColorPicker, TextAlignmentControls, ToggleButton } from './BlockToolbar';

interface Props {
    block: OfferBlock,
    readOnly?: boolean;
    onBlockUpdate: (updatedBlock: OfferBlock) => void;
    onBlockDelete: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    autoFocus?: boolean;
}

export default function BlockHeading(props: Props) {
    const { block: generalBlock, onBlockUpdate } = props;
    const block = generalBlock as OfferBlockHeading;
    const contentRef = useRef<HTMLElement>(null);
    const [showToolbar, setShowToolbar] = useState(false);

    useEffect(() => {
        if (props.autoFocus && contentRef.current) {
            contentRef.current.click();
            contentRef.current.focus();
        }
    }, [props.autoFocus]);

    const handleBlockUpdate = (update: Partial<OfferBlockHeading>) => {
        const updatedBlock = {
            ...block,
            ...update
        };
        updatedBlock.content = (updatedBlock.content || '').replace(/\n/, '<br />');
        onBlockUpdate(updatedBlock);
    }

    return (
        <div className="position-relative w-100">
            <BlockToolbar
                contentRef={contentRef}
                onDelete={props.onBlockDelete}
                onMoveUp={props.onMoveUp}
                onMoveDown={props.onMoveDown}
                showToolbar={showToolbar}
                setShowToolbar={setShowToolbar}
                readOnly={props.readOnly}
            >
                <HeadingLevelPicker
                    value={block.level || 'h2'}
                    onChange={(level) => handleBlockUpdate({ level })}
                />

                <ColorPicker
                    value={block.color || '#000000'}
                    onChange={(color) => handleBlockUpdate({ color })}
                />

                <TextAlignmentControls
                    value={block.textAlign}
                    onChange={(textAlign) => handleBlockUpdate({ textAlign })}
                />

                <ToggleButton
                    icon="bi-hr"
                    active={!!block.showLine}
                    onChange={(showLine) => handleBlockUpdate({ showLine })}
                />
            </BlockToolbar>

            <div className={`${block.showLine ? 'border-bottom pb-2 mb-2' : ''}`}>
                <ContentEditable
                    innerRef={contentRef}
                    html={block.content || ''}
                    onChange={(e) => handleBlockUpdate({ content: e.target.value })}
                    onClick={props.readOnly ? undefined : () => setShowToolbar(true)}
                    className='py-1'
                    disabled={props.readOnly}
                    tagName={block.level || 'h2'}
                    style={{
                        margin: 0,
                        color: block.color || undefined,
                        textAlign: block.textAlign || undefined,
                        userSelect: 'text', // Enable text selection within the content
                    }}
                />
            </div>
        </div>
    );
}
